import { LineChartResultResponse } from '@business/entities/dashboard';
import { R } from '@core/utils/r';
import 'chartist/dist/scss/chartist.scss';
import React, { memo, useMemo } from 'react';
import { Card, CardTitle } from 'reactstrap';
import { WidgetProps } from '../types';
import './index.scss';
import { WidgetIcons } from '../BaseWidget';
import ReactEcharts, { EChartsOption } from 'echarts-for-react';
import { LineChartWidgetConfigData } from './LineChartWidgetConfig';
import { formControl } from '@core/utils/form';
import { GraphTypes } from '@business/entities/graph';
import { FormSelect } from '@core/components/form';
import { CommonService, GraphService } from '@business/services';
import { durationHumanizeReports } from '@core/utils/time';

export const LineChartWidget = memo((props: WidgetProps<LineChartResultResponse, LineChartWidgetConfigData>) => {
  const { settings, result, config, isPreview = false } = props;
  const print = props.print || false;
  const control = useMemo(() => formControl<GraphTypes>({ initialValue: R.get(props, 'data.currentGraphType') || GraphTypes.daily }), [props]);

  const isUpsideDown = useMemo(() => {
    return config?.upside || false;
  }, [config]);

  const graphItems = useMemo(() => {
    return CommonService.selectItems(
      GraphService.getTypes(GraphTypes.daily),
      i => R.capitalizeWords(i),
      i => i,
    );
  }, []);

  const legend = useMemo(() => {
    if (!config) return undefined;
    if (!config.legends) return undefined;
    if (!result) return undefined;
    return {
      data: result ? result.series.map(s => R.get(s, '0.meta') || '') : [],
      bottom: '0%', // Position the legend above the chart
      itemWidth: 14, // Set marker width
      itemHeight: 14, // Set marker height
      itemGap: 10, // Increase space between legend items
      textStyle: {
        fontSize: 12, // Adjust font size
        padding: [0, 0, 0, 0], // Add padding between marker and text
      },
    };
  }, [config, result]);

  const colors = useMemo(() => {
    const defaultColors = ['#556ee6', '#FF9800', '#607D8B', '#00C7B5', '#03A9F4', '#9575CD', '#F06292'];
    if (!result) return defaultColors;
    if (!config) return defaultColors;
    if (!config.colors) return defaultColors;
    if (!config.colors.length) return defaultColors;
    return result.series.map((s, i) => {
      if (R.get(s, '0.meta')) {
        return config.colors.find((c: any) => c.label === R.get(s, '0.meta'))?.color || defaultColors[i];
      } else {
        return defaultColors[i];
      }
    });
    return config.colors.map(c => c.color);
  }, [result, config]);

  const redBlackLines = useMemo(() => {
    if (!result) return [];
    if (R.get(config, 'noRedBlackLines'))
      return result.series.map(s => ({
        label: R.get(config, 'showValuesOnGraph') ? { show: true, position: 'top' } : { show: false },
        type: 'line',
        name: R.get(s, '0.meta'),
        data: s.map(a => a.value),
      }));

    return result.newSeries.map(s => ({
      type: 'custom',
      label: R.get(config, 'showValuesOnGraph') ? { show: true, position: 'top' } : { show: false },

      data: s.map((f: any) => ({ value: f })),
      name: '',
      renderItem: function (_: any, api: any) {
        const start = api.coord([api.value(0), api.value(1)]);
        const end = api.coord([api.value(2), api.value(3)]);
        if ((R.isNaN(start[1]) || R.isNull(start[1])) && (R.isNaN(end[1]) || R.isNull(end[1]))) {
          return null;
        }
        return {
          type: 'group',
          children: [
            {
              type: 'circle',
              shape: {
                cx: 0,
                cy: 0,
                r: print ? 6 : 3,
              },
              style: {
                opacity: 1,
                stroke: start[1] < end[1] ? 'red' : 'black',
                lineWidth: 2,
                fill: 'white',
              },
              x: start[0],
              y: start[1],
              z2: 1,
            },
            {
              type: 'line',
              shape: {
                x1: start[0],
                y1: start[1],
                x2: end[0],
                y2: end[1],
              },
              z2: 0,
              style: {
                lineWidth: print ? 4 : 2,
                stroke: start[1] < end[1] ? 'red' : 'black',
              },
            },
          ],
        };
      },
    }));
  }, [result, print, config]);

  const options: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value: any) => {
        if (result?.label == 'd') {
          return durationHumanizeReports((R.toNumber(value) || 0) * 60);
        } else if (result?.label == 'h') {
          return durationHumanizeReports((R.toNumber(value) || 0) * 60, { units: ['h'] });
        } else if (result?.label == 'm') {
          return durationHumanizeReports((R.toNumber(value) || 0) * 60, { units: ['m'] });
        }
        return value;
      },
    },
    grid: {
      zlevel: 0,
      x: 50,
      x2: 50,
      y: 30,
      y2: 30,
      borderWidth: 0,
      containLabel: true,
      left: 0,
      bottom: config && config.legends ? '15%' : undefined,
    },
    xAxis: {
      type: 'category',
      data: result ? result.labels : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      axisLable: {
        color: '#ffffff',
      },
      axisLine: {
        lineStyle: {
          color: '#74788d',
        },
      },
    },
    yAxis: {
      inverse: isUpsideDown,
      type: 'value',
      min: function (value: any) {
        if (config && !R.isUndefined(config.minValue) && !R.isEmpty(config.minValue)) {
          return R.toNumber(config.minValue);
        }
        if (CommonService.isYesa()) {
          return value.min - 1 < 0 ? value.min - 1 : 0;
        }
        return value.min - 1;
      },
      max: function (value: any) {
        if (config && !R.isUndefined(config.maxValue) && !R.isEmpty(config.maxValue)) {
          return R.toNumber(config.maxValue);
        }

        if (CommonService.isYesa()) {
          const range = value.max - value.min;
          const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
          const step = Math.ceil(range / 5 / magnitude) * magnitude;
          return Math.ceil(value.max / step) * step;
        }
        return value.max + 1;
      },
      axisLable: {
        color: '#ffffff',
      },
      axisLine: {
        lineStyle: {
          color: '#74788d',
        },
      },
    },
    legend,
    series: result
      ? redBlackLines
      : [
          {
            data: isPreview ? [620, 832, 750, 934, 1290, 1330, 1400] : [],
            type: 'line',
          },
        ],
    color: colors,
    textStyle: {
      color: ['#74788d'],
    },
  };

  return (
    <Card className="p-4" style={{ marginBottom: 0, flex: 1 }}>
      <CardTitle className="mb-4 d-flex align-items-center flex-row">
        {settings.title || 'Line Chart'}
        <WidgetIcons {...props} />
        {!props.print && (
          <div className="ml-2" style={{ width: 150 }}>
            <FormSelect
              control={control}
              withoutGroup
              items={graphItems}
              onChange={type => props.onUpdateGraphType && props.onUpdateGraphType(type!)}
            />
          </div>
        )}
      </CardTitle>
      <ReactEcharts style={{ height: '100%', width: '100%' }} option={options} notMerge />
      {/* <ChartistGraph data={lineChartData} options={lineChartOptions} type={'Line'} style={{ height: settings.grid.h * 100 - 50 }} /> */}
    </Card>
  );
});
